import type * as React from "react";
import { cn } from "../../lib/utils";

/**
 * Przełącznik dwustanowy.
 *
 * **Natywny `input type="checkbox"` z `role="switch"`, nie primityw Radiksa.**
 * Radix daje tu dokładnie jedno: własny element do stylowania. Obsługę klawiatury
 * (spacja), stan dla czytnika ekranu (`aria-checked` wynika z `checked`) i wiązanie
 * z etykietą przez `<label>` mamy od przeglądarki, a `role="switch"` zmienia tylko
 * to, jak czytnik ekranu **nazywa** kontrolkę — z „pole wyboru" na „przełącznik".
 *
 * Zysk jest konkretny: zero kilobajtów w bundlu i zero zależności dla komponentu,
 * który ma dwa stany. Wygląd robi `peer` na ukrytym polu — to samo, co robi Radix,
 * tylko bez warstwy pośredniej.
 */
export function Switch({
  className,
  label,
  hint,
  checked,
  ...props
}: Omit<React.ComponentProps<"input">, "type" | "checked"> & {
  /** Etykieta jest OBOWIĄZKOWA: przełącznik bez opisu nie mówi, co przełącza. */
  label: string;
  hint?: string;
  /**
   * Stan jest WYMAGANY, bo `role="switch"` musi nieść `aria-checked`, a jedynym
   * uczciwym źródłem tej wartości jest kontrolka sterowana. Wersja niesterowana
   * podawałaby czytnikowi ekranu stan, który po pierwszym kliknięciu przestaje być
   * prawdziwy.
   */
  checked: boolean;
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3">
      <input
        type="checkbox"
        role="switch"
        checked={checked}
        /*
          `aria-checked` wprost, choć dla pola wyboru wynika ono ze stanu `checked`.
          Powód jest praktyczny, nie formalny: `role="switch"` zmienia sposób, w jaki
          czytnik ekranu nazywa kontrolkę, a część z nich przy podmienionej roli
          przestaje wyprowadzać stan z DOM-u. Kontrolka jest sterowana, więc obie
          wartości pochodzą z jednego źródła i nie mają jak się rozjechać.
        */
        aria-checked={checked}
        className={cn("peer sr-only", className)}
        {...props}
      />

      {/*
        Tor i suwak. `aria-hidden`, bo stan niesie już samo pole — czytnik ekranu
        odczytałby inaczej tę samą informację dwa razy.
      */}
      <span
        aria-hidden="true"
        /*
          Suwak przesuwamy przez wariant na RODZEŃSTWIE, nie na samym suwaku:
          `peer-*` generuje selektor `.peer:checked ~ …`, czyli działa dla rodzeństwa
          pola i jego potomków tylko wtedy, gdy klasa siedzi na rodzeństwie. Klasa
          `peer-checked:translate-x-4` postawiona wprost na wewnętrznym `span` nie
          zadziałałaby wcale, a przełącznik wyglądałby na zepsuty.
        */
        className="relative mt-0.5 inline-flex h-5 w-9 shrink-0 items-center rounded-full bg-secondary transition-colors duration-150 peer-checked:bg-primary peer-checked:[&>span]:translate-x-4 peer-focus-visible:ring-[0.1875rem] peer-focus-visible:ring-ring/50 peer-disabled:opacity-50"
      >
        <span className="absolute left-0.5 size-4 rounded-full bg-foreground/90 transition-transform duration-150" />
      </span>

      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="text-sm font-medium">{label}</span>
        {hint !== undefined && <span className="text-xs text-muted-foreground">{hint}</span>}
      </span>
    </label>
  );
}
